import {
  call, put, takeLatest,
} from 'redux-saga/effects';
import { isTwoFaActive } from '~/_shared/components/accountSettings/twoFactorModal/twoFactor.helpers';
import { checkIfModalIsAlreadyConfirmed } from '~/_shared/components/modal/confirmation/confirmationModal.helpers';
import { PermanentConfirmStrategy } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import {
  destroyBroadcast, initializeBroadcast,
} from '~/_shared/utils/api/broadcast.helpers';
import { select } from '~/_shared/utils/saga/effects';
import { StorageService } from '~/_shared/utils/storageService';
import { type PickAction } from '~/_shared/utils/types/action.type';
import { AuthBroadcastChannel } from '~/authorization/authContext/auth.utils';
import { ModalType } from '~/modal/modalType.enum';
import { CLIENT_SUBSCRIPTION_CHANGED } from '~/store/frontendState/client/client.actionTypes';
import { openModal } from '~/store/modal/modal.actionCreators';
import {
  getUserData, signOutUser, type TwoFactorStats, type UserDataResponse,
} from './repository/userData.repository';
import { type UserDataAction } from './userData.action';
import {
  userGetDataError, userGetDataRequest, userGetDataSuccess, userSignOutSuccess,
} from './userData.actionCreators';
import {
  USER_GET_DATA_REQUEST, USER_GET_DATA_SUCCESS, USER_RESTORE_DATA, USER_SIGN_OUT_REQUEST,
} from './userData.actionTypes';
import { userDataServerToClientModel } from './userData.dataMapping';

function* onRefreshUserData() {
  yield put(userGetDataRequest());
}

function* onUserGetDataRequest(action: PickAction<UserDataAction, typeof USER_GET_DATA_REQUEST>) {
  try {
    const userData: UserDataResponse = yield call(getUserData);
    initializeBroadcast({
      userId: userData.data.id,
    });

    const clientModel = userDataServerToClientModel(userData);
    yield put(userGetDataSuccess(clientModel, action.payload.options));
  }
  catch (e) {
    yield put(userGetDataError(e.message));
  }
}

function* onUserSignOut(action: PickAction<UserDataAction, typeof USER_SIGN_OUT_REQUEST>) {
  destroyBroadcast();

  try {
    yield call(signOutUser);
    AuthBroadcastChannel.postMessage({ type: 'logout' });
    action.payload.onSuccess?.();
    yield put(userSignOutSuccess());
  }
  catch (e) {
    console.error('Error while signing out', e);
  }
}

export const TWO_FA_WITH_SSO_PERMANENT_CONFIRM_ID = '2fa-sso-hint-dismissed';

function* onUserGetDataSuccess(action: PickAction<UserDataAction, typeof USER_GET_DATA_SUCCESS>) {
  if (!action.payload.options.isSsoLogin) {
    return;
  }

  const twoFaStats: TwoFactorStats = yield select(state => state.userData['2fa']);
  const storageService: StorageService = yield call(getUserStorageService);

  const isAlreadyConfirmed = checkIfModalIsAlreadyConfirmed(
    { id: TWO_FA_WITH_SSO_PERMANENT_CONFIRM_ID, strategy: PermanentConfirmStrategy.Local },
    storageService,
  );

  if (!isAlreadyConfirmed && isTwoFaActive(twoFaStats)) {
    yield put(openModal(ModalType.TwoFaWithSso));
  }
}

export function* getUserStorageService() {
  const userId: number | null = yield select<number | null>(state => state.userData.id);

  return new StorageService(userId);
}

export function* userDataSagas() {
  yield takeLatest(USER_RESTORE_DATA, onRefreshUserData);
  yield takeLatest(CLIENT_SUBSCRIPTION_CHANGED, onRefreshUserData);
  yield takeLatest(USER_GET_DATA_REQUEST, onUserGetDataRequest);
  yield takeLatest(USER_SIGN_OUT_REQUEST, onUserSignOut);
  yield takeLatest(USER_GET_DATA_SUCCESS, onUserGetDataSuccess);
}
